/* eslint-disable @typescript-eslint/no-explicit-any */
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ChangeDetectorRef,
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  twoFactorCancelledError,
  twoFactorMaxAttemptsError,
} from '@gatewee/services/two-factor-auth.service';
import {resolveCompeDisplayName} from '@gatewee/helpers';
import {
  PixRecipientResponse,
  PixRecipientService,
  readProfileRaw,
} from '@gatewee/services';
import {SettingsService} from '@gatewee/services/core/settings.service';
import {PaymentReceiptExportService} from '@gatewee/components/v2';
import {
  FilterOptions,
  GenericCrudService,
} from '@gatewee/services/generic-crud.service';
import {PaymentProviderResponse, PaymentReceipt} from '@gatewee/types/payment';
import {
  PixKeyType as PixKeyTypeType,
  RecipientBankAccount,
  RecipientBankAccountStatus,
} from '@gatewee/types/recipient-bank-account';
import {PublicSettings} from '@gatewee/types/system';
import {Wallet} from '@gatewee/types/wallet';
import {
  RecipientLimitsSnapshot,
  RecipientSettingKey,
} from '@gatewee/types/recipient-setting';
import {FeeSnapshot} from '@gatewee/types/fee';
import {Withdrawal, WithdrawalStatus} from '@gatewee/types/withdrawal';
import {
  calculateWithdrawalAmounts,
  extractPaymentMetadataError,
  getWithdrawalStatusErrorMessage,
  resolveWithdrawalAmounts,
  WithdrawalAmountBreakdown,
  WithdrawalAmountInputMode,
} from '@gatewee/utils';
import {
  cpfCnpjFormattedMaxLength,
  cpfCnpjNoLiveMask,
  formatCpfCnpjDisplay,
} from '@gatewee/utils/helpers/cpf-cnpj-input.helper';
import {
  formatPixKeyForDisplay,
  inferPixKeyKind as inferRecipientKind,
  PixKeyInferKind as RecipientInferKind,
  sanitizePixKeyForApi,
} from '@gatewee/utils/helpers/pix-key-infer.helper';
import {validateCPFOrCNPJ} from '@gatewee/utils/validators/cpf-cnpjs-validators';
import {TranslateService} from '@ngx-translate/core';
import {TuiDay} from '@taiga-ui/cdk';
import {TuiAlertService} from '@taiga-ui/core';
import {EMPTY, interval, merge, of, Subject, Subscription} from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  switchMap,
  tap,
} from 'rxjs/operators';
import {RecipientsSettingsService} from '../../../../_services/settings/recipients-settings.service';
import {WithdrawalsService} from '../../withdrawals.service';
import {
  formatRecipientAccount,
  formatRecipientAgency,
  mapKindToPixKeyType,
  validateRecipientByKind,
} from './withdrawal-pix-recipient.utils';
import {PixKeyPipe} from '@gatewee/utils/pipes/pix-key.pipe';
import {PixEmvDecoded} from '@gatewee/utils/validators/pix-emv.validator';

interface PayloadPix {
  amount: number;
  description?: string;
  emvCode?: string;
  recipientBankAccountId?: number;
  bankAccount?: {
    pixKey: string;
    pixKeyType: string;
    holderDocument: string;
    pixKeyE2eId?: string;
  };
}

type EmvHandoffContext = {
  emvCode: string;
  decoded: PixEmvDecoded;
  amountInputMode: WithdrawalAmountInputMode;
};

/** Key for text search inside `options` passed to `findAll` (serialized as JSON on `options`); align with API if renamed. */
const bankAccountsSearchParam = 'search';

export type RecipientFlowStep =
  | 'unlocked'
  | 'confirmed'
  | 'review'
  | 'value'
  | 'payment_proof';

export type PixTransferShellKind = 'dialog' | 'pageDrawer';

/**
 * PIX transfer wizard: one `FormGroup`, step driven by `recipientStepStatus`.
 * **Submit / Enter:** only the review step should post; use `onWizardFormSubmit` on the
 * form so other steps do not trigger `onSubmit` when the user presses Enter.
 * (Send is also triggered explicitly from the review step button.)
 */
@Component({
  selector: 'app-withdrawal-transfer-form',
  templateUrl: './withdrawal-transfer-form.component.html',
  styleUrls: ['./withdrawal-transfer-form.component.css'],
  standalone: false,
})
export class WithdrawalTransferFormComponent implements OnInit, OnChanges {
  /** When false (hub visible), bank-account search stream is gated off. */
  @Input() flowActive = false;

  /** `dialog`: in-form sticky footer for CTAs. `pageDrawer`: CTAs projected into `gatewee-drawer` footer. */
  @Input() pixShellKind: PixTransferShellKind = 'dialog';

  /**
   * When true, non-document PIX keys require holder CPF/CNPJ on the value step.
   * Document keys mirror `pixKey` into `document` without showing the extra field.
   */
  @Input() requireHolderDocument = true;

  /**
   * When true, skip checkKey and the confirmed recipient step; go unlocked → value.
   */
  @Input() skipPixRecipientVerification = false;

  /** Effective fee snapshot from parent (`PixAreaComponent`). */
  @Input() feeSnapshot: FeeSnapshot | null = null;

  readonly holderDocumentMaxLength = cpfCnpjFormattedMaxLength;

  @ViewChild('transferActionFooter', {read: TemplateRef})
  private transferActionFooterTplRef?: TemplateRef<unknown>;

  /** Used by `PixAreaComponent` for the page-drawer footer slot. */
  get actionFooterTpl(): TemplateRef<unknown> | null {
    return this.transferActionFooterTplRef ?? null;
  }

  @Output() readonly completed = new EventEmitter<void>();
  @Output() readonly cancelled = new EventEmitter<void>();

  /** User pressed back on value step while in an EMV handoff; parent returns to EMV review. */
  @Output() readonly emvHandoffBack = new EventEmitter<void>();

  /** Emits whenever `currentTitleKey` changes (drawer / embedded header). */
  @Output() readonly embeddedTitleKeyChange = new EventEmitter<string>();

  /** Notifies shell (page drawer) to refresh projected footer bindings. */
  @Output() readonly shellFooterChange = new EventEmitter<void>();

  /** Active when user continued from EMV review into the transfer value step. */
  emvHandoff: EmvHandoffContext | null = null;

  pixTransferForm!: FormGroup;
  isSubmitting = false;

  /** Abre o `tui-calendar` ao clicar no ícone (revisão “Quando”). */
  scheduleDateDropdownOpen = false;

  recipientMask = '';
  recipientInferredKind: RecipientInferKind = 'unknown';
  recipientStepStatus: RecipientFlowStep = 'unlocked';

  pixRecipientLookupPending = false;
  pixRecipientLookupErrorMessage: string | null = null;

  pixRecipientResponse: PixRecipientResponse | null = null;

  lastVerifiedKey = '';
  manualPixKeyKind: 'cpf' | 'phone' | null = null;
  pixKeyFocused = false;

  /** False until `WithdrawalStepValueComponent` mounts (avoids proceed before amount input exists). */
  valueStepViewReady = false;

  /** Snapshot from value step; survives review step when value view is destroyed. */
  private transferDescription = '';

  bankAccounts: RecipientBankAccount[] = [];
  bankAccountsLoading = false;
  contactSearchQuery = '';
  private savedBankAccountSelection: {
    id: number;
    pixKey: string;
    pixKeyType: string;
    holderDocument: string;
  } | null = null;
  private readonly pixTransferEntered$ = new Subject<void>();

  /** Withdrawal returned by create; refreshed by polling while pending/processing. */
  createdWithdrawal: Withdrawal | null = null;
  /** Provider/API error when create fails without a full withdrawal payload. */
  transferErrorMessage: string | null = null;
  private pollingSub?: Subscription;

  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly crudService: GenericCrudService<Withdrawal>;
  private readonly crudServiceBankAccounts: GenericCrudService<RecipientBankAccount>;
  private readonly walletsService: GenericCrudService<Wallet>;

  /** Primary wallet balance from `/wallets/recipient` (`isPrimary`, else first wallet). */
  walletAvailableBalance: number | null = null;

  /** Cash-out limits from `/recipients-settings/recipient/limits`. */
  recipientLimits: RecipientLimitsSnapshot | null = null;

  /** From businessRules: when true, user enters net payout (fee added on top by backend). */
  private addWithdrawalFeeToAmount = false;

  private readonly recipientsSettingsService = inject(
    RecipientsSettingsService,
  );

  constructor(
    private fb: FormBuilder,
    http: HttpClient,
    public translate: TranslateService,
    private alerts: TuiAlertService,
    private pixRecipientService: PixRecipientService,
    private readonly withdrawalsService: WithdrawalsService,
    private readonly pixKeyPipe: PixKeyPipe,
  ) {
    this.crudService = new GenericCrudService<Withdrawal>(
      '/withdrawals/recipient/pix',
      http,
    );
    this.crudServiceBankAccounts = new GenericCrudService<RecipientBankAccount>(
      '/recipients-bank-accounts/recipient',
      http,
    );
    this.walletsService = new GenericCrudService<Wallet>(
      '/wallets/recipient',
      http,
    );
    this.pixTransferForm = this.fb.group({
      pixKey: [
        '',
        [Validators.required, this.validatePixKeyDynamic.bind(this)],
      ],
      pixKeyType: [''],
      amount: [''],
      document: [''],
      description: [''],
      scheduledDate: [null as string | null],
      repeatTransfer: [false],
    });
  }

  /** i18n key for embedded drawer title while this flow is active. */
  get currentTitleKey(): string {
    switch (this.recipientStepStatus) {
      case 'confirmed':
        return 'features.withdrawals.modalPix.drawerTitleConfirmRecipient';
      case 'review':
        return 'features.withdrawals.modalPix.drawerTitleTransferReview';
      case 'value':
        return 'features.withdrawals.modalPix.drawerTitleTransferAmount';
      case 'payment_proof':
        return 'features.withdrawals.modalPix.drawerTitlePaymentProof';
      case 'unlocked':
      default:
        return 'features.withdrawals.modalPix.transferRecipientTitle';
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['flowActive']) {
      const prev = changes['flowActive'].previousValue;
      const cur = changes['flowActive'].currentValue;
      if (prev === true && cur === false) {
        this.cancelled.emit();
      }
      if (cur === true) {
        this.onFlowActivated();
      }
    }
    if (changes['feeSnapshot'] && this.recipientStepStatus === 'value') {
      this.pixTransferForm
        .get('amount')
        ?.updateValueAndValidity({emitEvent: false});
    }
  }

  private onFlowActivated(): void {
    if (this.emvHandoff) {
      this.emitEmbeddedTitleKey();
      return;
    }
    this.recipientStepStatus = 'unlocked';
    this.emitEmbeddedTitleKey();
    this.pixTransferForm?.get('amount')?.clearValidators();
    this.pixTransferForm?.get('document')?.clearValidators();
    this.pixTransferForm
      ?.get('amount')
      ?.updateValueAndValidity({emitEvent: false});
    this.pixTransferForm
      ?.get('document')
      ?.updateValueAndValidity({emitEvent: false});
    this.pixTransferEntered$.next();
  }

  private validatePixKeyDynamic(
    control: AbstractControl,
  ): ValidationErrors | null {
    const raw = String(control.value ?? '').trim();
    if (!raw) {
      return null;
    }
    const inferred = inferRecipientKind(raw);
    let kind: RecipientInferKind = inferred;
    if (this.manualPixKeyKind === 'cpf') {
      kind = 'cpf';
    } else if (this.manualPixKeyKind === 'phone') {
      kind = 'phone';
    }
    return validateRecipientByKind(raw, kind);
  }

  ngOnInit(): void {
    this.recipientMask = '';
    this.recipientInferredKind = 'unknown';
    this.addWithdrawalFeeToAmount = !!this.settingsService.get(
      'businessRules',
      RecipientSettingKey.ADD_WITHDRAWAL_FEE_TO_AMOUNT,
    );
    this.loadRecipientWallet();
    this.loadRecipientLimits();

    const pixKeyCtrl = this.pixTransferForm.get('pixKey');
    if (!pixKeyCtrl) {
      return;
    }

    pixKeyCtrl.valueChanges
      .pipe(
        tap(val => {
          const s = typeof val === 'string' ? val : '';
          const trimmed = s.trim();
          this.clearSavedBankAccountSelectionIfPixKeyChanged(trimmed);
          if (trimmed !== this.lastVerifiedKey) {
            this.pixRecipientResponse = null;
            this.pixRecipientLookupErrorMessage = null;
          }

          const kind = inferRecipientKind(s);
          this.recipientInferredKind = kind;

          if (kind !== 'cpf_or_phone') {
            this.manualPixKeyKind = null;
          }

          const typeCtrl = this.pixTransferForm.get('pixKeyType');
          if (!this.manualPixKeyKind) {
            const mapped =
              kind === 'cpf_or_phone' ? '' : mapKindToPixKeyType(kind);
            if (mapped !== (typeCtrl?.value ?? '')) {
              typeCtrl?.setValue(mapped, {emitEvent: false});
            }
          }

          this.recipientMask = '';
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    merge(
      this.pixTransferEntered$.pipe(
        map(() => String(pixKeyCtrl.value ?? '').trim()),
      ),
      pixKeyCtrl.valueChanges.pipe(
        debounceTime(800),
        map(v => String(v ?? '').trim()),
        distinctUntilChanged(),
      ),
    )
      .pipe(
        filter(
          () => this.flowActive && this.recipientStepStatus === 'unlocked',
        ),
        distinctUntilChanged(),
        tap(q => {
          this.contactSearchQuery = q;
          this.bankAccountsLoading = true;
        }),
        switchMap(q => {
          const filters: FilterOptions | undefined = q
            ? {options: {[bankAccountsSearchParam]: q.toString()}}
            : undefined;
          return this.crudServiceBankAccounts.findAll(filters, true).pipe(
            catchError(() => of([] as RecipientBankAccount[])),
            finalize(() => {
              this.bankAccountsLoading = false;
            }),
          );
        }),
        tap(accounts => {
          this.bankAccounts = accounts ?? [];
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onPixKeyFocus(): void {
    this.pixKeyFocused = true;
  }

  onPixKeyBlur(): void {
    this.pixKeyFocused = false;
    this.formatPixKeyFieldOnBlur();
  }

  /** Applies display formatting once on blur (no ngx-mask while typing). */
  private formatPixKeyFieldOnBlur(): void {
    const ctrl = this.pixTransferForm.get('pixKey');
    const raw = String(ctrl?.value ?? '');
    const trimmed = raw.trim();
    if (!trimmed) {
      return;
    }

    const kind = inferRecipientKind(trimmed);
    const formatted = formatPixKeyForDisplay(raw, kind, this.manualPixKeyKind);
    if (formatted !== raw) {
      ctrl?.setValue(formatted, {emitEvent: true});
    }
  }

  verifyRecipient(): void {
    const ctrl = this.pixTransferForm.get('pixKey');
    if (
      !ctrl ||
      this.pixRecipientLookupPending ||
      this.recipientStepStatus !== 'unlocked'
    ) {
      return;
    }

    const raw = String(ctrl.value ?? '');
    const trimmed = raw.trim();
    if (!trimmed) {
      return;
    }

    if (
      this.recipientInferredKind === 'cpf_or_phone' &&
      !this.manualPixKeyKind
    ) {
      this.pixRecipientLookupErrorMessage = this.translate.instant(
        'features.withdrawals.modalPix.kindAmbiguousPickFirst',
      );
      return;
    }

    ctrl.updateValueAndValidity({emitEvent: false});
    if (ctrl.invalid) {
      ctrl.markAsTouched();
      return;
    }

    if (trimmed === this.lastVerifiedKey && this.pixRecipientResponse) {
      this.pixRecipientLookupErrorMessage = null;
      this.applyDocumentRulesForRecipient();
      if (this.skipPixRecipientVerification) {
        this.goToValueStep();
      } else {
        this.recipientStepStatus = 'confirmed';
        this.emitEmbeddedTitleKey();
      }
      return;
    }

    if (this.skipPixRecipientVerification) {
      this.proceedWithoutPixVerification(trimmed);
      return;
    }

    this.runCheckKeyVerification(trimmed);
  }

  /** Same outcome as when PIX recipient verification is disabled: proceed without API data. */
  private proceedWithoutPixVerification(trimmedKey: string): void {
    this.pixRecipientLookupErrorMessage = null;
    this.pixRecipientResponse = null;
    this.lastVerifiedKey = trimmedKey;
    this.applyDocumentRulesForRecipient();
    this.goToValueStep();
  }

  private runCheckKeyVerification(trimmedKey: string): void {
    const formPixKeyType = String(
      this.pixTransferForm.get('pixKeyType')?.value ?? '',
    ).trim();
    if (!formPixKeyType) {
      this.pixRecipientLookupErrorMessage = this.translate.instant(
        'features.withdrawals.modalPix.kindAmbiguousPickFirst',
      );
      return;
    }

    let apiPixKeyType: PixKeyTypeType;
    try {
      apiPixKeyType = this.transformPixKeyType(formPixKeyType);
    } catch {
      this.pixRecipientLookupErrorMessage = this.translate.instant(
        'features.withdrawals.modalPix.kindAmbiguousPickFirst',
      );
      return;
    }

    const sanitizedKey = this.sanitizePixKeyForPayload(
      trimmedKey,
      formPixKeyType,
    );

    this.pixRecipientLookupErrorMessage = null;
    this.pixRecipientLookupPending = true;
    this.pixRecipientService
      .checkKey(sanitizedKey, apiPixKeyType)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.pixRecipientLookupPending = false;
        }),
        catchError(() => {
          this.proceedWithoutPixVerification(trimmedKey);
          return EMPTY;
        }),
      )
      .subscribe({
        next: res => this.applyCheckKeyResult(res, trimmedKey),
      });
  }

  private applyCheckKeyResult(
    res: PixRecipientResponse | null,
    trimmedKey: string,
  ): void {
    if (!res) {
      this.proceedWithoutPixVerification(trimmedKey);
      return;
    }
    this.pixRecipientResponse = res;
    this.pixRecipientLookupErrorMessage = null;
    this.lastVerifiedKey = trimmedKey;
    this.applyDocumentRulesForRecipient();
    this.recipientStepStatus = 'confirmed';
    this.emitEmbeddedTitleKey();
  }

  pickAmbiguousKind(kind: 'cpf' | 'phone'): void {
    this.manualPixKeyKind = kind;
    this.pixTransferForm.get('pixKeyType')?.setValue(kind, {emitEvent: false});
    this.formatPixKeyFieldOnBlur();
    this.pixTransferForm.get('pixKey')?.updateValueAndValidity({
      emitEvent: false,
    });
    this.pixRecipientLookupErrorMessage = null;
    if (!this.skipPixRecipientVerification) {
      void this.verifyRecipient();
    }
  }

  get effectiveRecipientKind(): RecipientInferKind {
    if (this.manualPixKeyKind === 'cpf') {
      return 'cpf';
    }
    if (this.manualPixKeyKind === 'phone') {
      return 'phone';
    }
    return this.recipientInferredKind;
  }

  get showAmbiguousKindRow(): boolean {
    return this.recipientInferredKind === 'cpf_or_phone';
  }

  get needsAmbiguousKindChoice(): boolean {
    return (
      this.recipientInferredKind === 'cpf_or_phone' && !this.manualPixKeyKind
    );
  }

  get canVerifyRecipient(): boolean {
    const c = this.pixTransferForm.get('pixKey');
    return !!(
      c &&
      !this.pixRecipientLookupPending &&
      c.valid &&
      !this.needsAmbiguousKindChoice
    );
  }

  get pixKeyFieldShowsError(): boolean {
    const c = this.pixTransferForm.get('pixKey');
    return !!(c?.invalid && (c.dirty || c.touched));
  }

  get recipientInputType(): 'text' | 'email' {
    return this.effectiveRecipientKind === 'email' ? 'email' : 'text';
  }

  get recipientInputIconName(): string {
    switch (this.effectiveRecipientKind) {
      case 'cpf':
      case 'cpf_partial':
      case 'cnpj':
      case 'cpf_or_phone':
        return '@phi.id-card';
      case 'phone':
      case 'phone_partial':
        return '@phi.phone';
      case 'email':
        return '@phi.envelope';
      case 'random':
        return '@phi.key';
      default:
        return '@phi.key';
    }
  }

  get defaultBankAccount(): RecipientBankAccount | null {
    return this.bankAccounts.find(a => a.default) ?? null;
  }

  bankDisplayNameForAccount(account: RecipientBankAccount): string {
    const code = account.compeCode;
    if (!code) {
      return `PIX: ${account.pixKey}`;
    }
    return resolveCompeDisplayName(code);
  }

  selectContact(account: RecipientBankAccount): void {
    if (account.status !== RecipientBankAccountStatus.ACTIVE) {
      return;
    }
    const pixKey = String(account.pixKey ?? '').trim();
    if (!pixKey) {
      return;
    }
    const formKeyType = this.mapRecipientBankAccountToFormPixKeyType(account);
    this.savedBankAccountSelection = {
      id: account.id,
      pixKey,
      pixKeyType: formKeyType,
      holderDocument: String(account.holderDocument ?? ''),
    };
    const kind = inferRecipientKind(pixKey);
    this.recipientInferredKind = kind;
    this.manualPixKeyKind =
      formKeyType === 'cpf' || formKeyType === 'phone' ? formKeyType : null;
    this.recipientMask = '';

    this.pixRecipientLookupErrorMessage = null;
    this.lastVerifiedKey = '';

    this.pixTransferForm.patchValue(
      {pixKey, pixKeyType: formKeyType},
      {emitEvent: false},
    );
    this.formatPixKeyFieldOnBlur();

    this.pixRecipientResponse = this.recipientResponseFromBankAccount(account);
    this.applyDocumentRulesForRecipient();
    this.recipientStepStatus = 'confirmed';
    this.emitEmbeddedTitleKey();
  }

  private recipientResponseFromBankAccount(
    account: RecipientBankAccount,
  ): PixRecipientResponse {
    return {
      holderName: account.holderName,
      holderDocument: account.holderDocument,
      compeCode: account.compeCode,
      agency: account.agency,
      account: account.account,
    };
  }

  private mapRecipientBankAccountToFormPixKeyType(
    account: RecipientBankAccount,
  ): string {
    switch (account.pixKeyType) {
      case PixKeyTypeType.DOCUMENT: {
        const d = String(account.pixKey ?? '').replace(/\D/g, '');
        if (d.length === 14) {
          return 'cnpj';
        }
        return 'cpf';
      }
      case PixKeyTypeType.PHONE:
        return 'phone';
      case PixKeyTypeType.EMAIL:
        return 'email';
      case PixKeyTypeType.RANDOM_KEY:
        return 'random';
      default:
        return '';
    }
  }

  private applyDocumentRulesForRecipient(): void {
    const pixKeyCtrl = this.pixTransferForm.get('pixKey');
    if (!pixKeyCtrl) {
      return;
    }
    const type = String(this.pixTransferForm.get('pixKeyType')?.value ?? '');
    const pixKey = String(pixKeyCtrl.value ?? '');
    const digits = pixKey.replace(/\D/g, '');

    const docCtrl = this.pixTransferForm.get('document');
    if (type === 'cpf' || type === 'cnpj') {
      docCtrl?.setValue(digits, {emitEvent: false});
      docCtrl?.clearValidators();
    } else if (this.requireHolderDocument) {
      const savedDoc = String(
        this.savedBankAccountSelection?.holderDocument ?? '',
      ).replace(/\D/g, '');
      const apiDoc = String(
        this.pixRecipientResponse?.holderDocument ?? '',
      ).replace(/\D/g, '');
      const resolvedDoc = savedDoc.length > 0 ? savedDoc : apiDoc;
      if (resolvedDoc.length > 0) {
        docCtrl?.setValue(resolvedDoc, {emitEvent: false});
        docCtrl?.clearValidators();
      } else {
        docCtrl?.setValue('', {emitEvent: false});
        docCtrl?.setValidators([Validators.required, validateCPFOrCNPJ]);
      }
    } else {
      docCtrl?.setValue('', {emitEvent: false});
      docCtrl?.clearValidators();
    }
    docCtrl?.updateValueAndValidity({emitEvent: false});
  }

  get shouldAskHolderDocumentInValueStep(): boolean {
    if (this.emvHandoff) {
      return false;
    }
    const type = String(this.pixTransferForm.get('pixKeyType')?.value ?? '');
    if (!this.requireHolderDocument || type === 'cpf' || type === 'cnpj') {
      return false;
    }
    const savedDoc = String(
      this.savedBankAccountSelection?.holderDocument ?? '',
    ).trim();
    if (savedDoc.length > 0) {
      return false;
    }
    const apiDoc = String(
      this.pixRecipientResponse?.holderDocument ?? '',
    ).trim();
    if (apiDoc.length > 0) {
      return false;
    }
    return true;
  }

  get holderDocumentMask(): string {
    return cpfCnpjNoLiveMask;
  }

  formatHolderDocumentOnBlur(): void {
    const ctrl = this.pixTransferForm.get('document');
    if (!ctrl) {
      return;
    }
    const formatted = formatCpfCnpjDisplay(ctrl.value);
    if (formatted !== String(ctrl.value ?? '')) {
      ctrl.setValue(formatted, {emitEvent: false});
    }
  }

  get holderDocumentCustomMessages(): Record<string, string> {
    const p = 'features.withdrawals.modalPix.customMessages';
    return {
      required: this.translate.instant(`${p}.holderDocumentRequired`),
      invalidDocument: this.translate.instant(`${p}.invalidDocument`),
      invalidCPF: this.translate.instant(`${p}.invalidCPF`),
      invalidCNPJ: this.translate.instant(`${p}.invalidCNPJ`),
    };
  }

  private emitEmbeddedTitleKey(): void {
    this.embeddedTitleKeyChange.emit(this.currentTitleKey);
  }

  /** Value step: proceed to transfer review. */
  goToReviewFromValue(): void {
    if (!this.canProceedFromValue) {
      this.pixTransferForm.get('amount')?.markAsTouched();
      if (this.shouldAskHolderDocumentInValueStep) {
        this.pixTransferForm.get('document')?.markAsTouched();
      }
      return;
    }
    this.valueStepViewReady = false;
    this.transferDescription = String(
      this.pixTransferForm.get('description')?.value ?? '',
    ).trim();
    this.recipientStepStatus = 'review';
    this.emitEmbeddedTitleKey();
  }

  /** Value step: cancel back to confirmed recipient (or unlocked when verification skipped). */
  backToConfirmedFromValue(): void {
    if (this.emvHandoff) {
      this.emvHandoffBack.emit();
      return;
    }
    this.recipientStepStatus =
      this.skipPixRecipientVerification || !this.pixRecipientResponse
        ? 'unlocked'
        : 'confirmed';
    this.emitEmbeddedTitleKey();
  }

  /** Review step: return to amount entry. */
  backToValueFromReview(): void {
    this.valueStepViewReady = false;
    this.recipientStepStatus = 'value';
    this.applyEmvFixedAmountLock();
    this.emitEmbeddedTitleKey();
    this.shellFooterChange.emit();
  }

  /** Keeps the amount control locked when the EMV payload defines a fixed value. */
  private applyEmvFixedAmountLock(): void {
    const amountCtrl = this.pixTransferForm.get('amount');
    if (this.hasEmvFixedAmount) {
      amountCtrl?.disable({emitEvent: false});
    }
  }

  /**
   * Disabled controls have status `DISABLED`, so `.valid` is false even with a preset value.
   */
  private isAmountControlValid(): boolean {
    const amountCtrl = this.pixTransferForm.get('amount');
    if (!amountCtrl) {
      return false;
    }
    const n = this.coerceAmount(amountCtrl.value);
    if (n === null || n < 0.01) {
      return false;
    }
    if (amountCtrl.disabled) {
      return true;
    }
    return amountCtrl.valid;
  }

  /**
   * Handles in-flow back (review → value → confirmed → unlocked). Returns `true` when
   * handled; parent should return to hub when `false`.
   */
  handleBack(): boolean {
    switch (this.recipientStepStatus) {
      case 'review':
        this.backToValueFromReview();
        return true;
      case 'value':
        this.backToConfirmedFromValue();
        return true;
      case 'confirmed':
        this.cancelConfirmedRecipient();
        return true;
      case 'payment_proof':
        return true;
      case 'unlocked':
      default:
        return false;
    }
  }

  get recipientBankDisplayName(): string {
    const recv = this.paymentReceipt?.receiver;
    if (recv) {
      const fromReceipt = resolveCompeDisplayName(
        recv.compeCode,
        recv.ispbCode,
      ).trim();
      if (fromReceipt) {
        return fromReceipt;
      }
    }
    return resolveCompeDisplayName(this.pixRecipientResponse?.compeCode);
  }

  get emvDecodedForReview(): PixEmvDecoded | null {
    return this.emvHandoff?.decoded ?? null;
  }

  get emvAmountReadOnly(): boolean {
    return this.hasEmvFixedAmount;
  }

  /** Value-step proceed: disabled EMV amount controls report `valid: false` in Angular. */
  get canProceedFromValue(): boolean {
    if (!this.valueStepViewReady) {
      return false;
    }
    if (
      this.shouldAskHolderDocumentInValueStep &&
      !this.pixTransferForm.get('document')?.valid
    ) {
      return false;
    }
    return this.isAmountControlValid();
  }

  onValueStepViewReady(): void {
    this.valueStepViewReady = true;
    this.shellFooterChange.emit();
    this.cdr.detectChanges();
    queueMicrotask(() => this.cdr.detectChanges());
  }

  get transferDescriptionForReview(): string {
    return (
      this.transferDescription ||
      String(this.pixTransferForm.get('description')?.value ?? '')
    );
  }

  private get hasEmvFixedAmount(): boolean {
    const amount = this.emvHandoff?.decoded.amount;
    return typeof amount === 'number' && Number.isFinite(amount);
  }

  /**
   * Starts the transfer flow at the value step using decoded EMV data (skips PIX key).
   * Call before activating the transfer shell flow (`flowActive = true`).
   */
  beginFromEmv(params: {
    emvCode: string;
    decoded: PixEmvDecoded;
    amountInputMode: WithdrawalAmountInputMode;
  }): void {
    this.emvHandoff = {
      emvCode: params.emvCode.trim(),
      decoded: params.decoded,
      amountInputMode: params.amountInputMode,
    };

    this.pixTransferForm.get('pixKey')?.clearValidators();
    this.pixTransferForm
      .get('pixKey')
      ?.updateValueAndValidity({emitEvent: false});
    this.pixTransferForm.get('document')?.clearValidators();
    this.pixTransferForm
      .get('document')
      ?.updateValueAndValidity({emitEvent: false});

    const amountCtrl = this.pixTransferForm.get('amount');
    const fixedAmount = params.decoded.amount;

    this.pixTransferForm.patchValue(
      {
        amount:
          typeof fixedAmount === 'number' && Number.isFinite(fixedAmount)
            ? fixedAmount
            : '',
      },
      {emitEvent: false},
    );

    this.goToValueStep();

    if (typeof fixedAmount === 'number' && Number.isFinite(fixedAmount)) {
      this.applyEmvFixedAmountLock();
    } else {
      amountCtrl?.enable({emitEvent: false});
    }
  }

  /** Clears EMV handoff state when returning to EMV review or resetting the shell. */
  clearEmvHandoff(): void {
    this.emvHandoff = null;
    this.recipientStepStatus = 'unlocked';
    this.pixTransferForm
      .get('pixKey')
      ?.setValidators([
        Validators.required,
        this.validatePixKeyDynamic.bind(this),
      ]);
    this.pixTransferForm.get('amount')?.clearValidators();
    this.pixTransferForm.get('amount')?.enable({emitEvent: false});
    this.pixTransferForm.patchValue(
      {
        pixKey: '',
        pixKeyType: '',
        amount: '',
        document: '',
      },
      {emitEvent: false},
    );
    this.pixTransferForm
      .get('pixKey')
      ?.updateValueAndValidity({emitEvent: false});
    this.pixTransferForm
      .get('amount')
      ?.updateValueAndValidity({emitEvent: false});
    this.emitEmbeddedTitleKey();
  }

  /** Safe holder name for any step when API omits `holderName`. */
  get resolvedRecipientName(): string {
    const emvName = String(this.emvHandoff?.decoded.merchantName ?? '').trim();
    if (emvName.length > 0) {
      return emvName;
    }

    const n = String(this.pixRecipientResponse?.holderName ?? '').trim();
    if (n.length > 0) {
      return n;
    }

    const pixKey = String(
      this.pixTransferForm.get('pixKey')?.value ?? '',
    ).trim();
    if (pixKey.length > 0) {
      return this.pixKeyPipe.transform(
        pixKey,
        String(this.pixTransferForm.get('pixKeyType')?.value ?? ''),
      );
    }
    return this.translate.instant(
      'features.withdrawals.modalPix.recipientNameUnavailable',
    );
  }

  /** Initials source for `tuiInitials` (never undefined access on API object). */
  get confirmedAvatarInitialsSource(): string {
    const n = String(this.pixRecipientResponse?.holderName ?? '').trim();
    return n.length > 0 ? n : '?';
  }

  get confirmedBankBadgeLetter(): string {
    const bankLine = this.recipientBankDisplayName.trim();
    if (bankLine.length > 0) {
      const ch = bankLine.charAt(0);
      if (/[\dA-Za-zÀ-ÿ]/.test(ch)) {
        return ch.toUpperCase();
      }
    }
    const code = this.pixRecipientResponse?.compeCode;
    if (code !== undefined && code !== null && String(code).trim().length > 0) {
      return String(code).trim().charAt(0);
    }
    return '?';
  }

  get confirmedBankDisplayLine(): string {
    const line = this.recipientBankDisplayName.trim();
    return line.length > 0
      ? line
      : this.translate.instant(
          'features.withdrawals.modalPix.valueUnavailable',
        );
  }

  get confirmedPixKeyDisplay(): string {
    return String(
      this.pixTransferForm.get('pixKey')?.value ?? this.lastVerifiedKey ?? '',
    ).trim();
  }

  get confirmedPixKeyLine(): string {
    const k = this.confirmedPixKeyDisplay;
    return k.length > 0
      ? k
      : this.translate.instant(
          'features.withdrawals.modalPix.valueUnavailable',
        );
  }

  /**
   * Sub-key under `common.pixKeyTypes` for the confirmed PIX row (email, phone, document, random_key).
   */
  get confirmedPixKeyCommonTypeKey():
    | 'email'
    | 'phone'
    | 'document'
    | 'random_key'
    | null {
    switch (this.effectiveRecipientKind) {
      case 'cpf':
      case 'cpf_partial':
      case 'cnpj':
        return 'document';
      case 'phone':
      case 'phone_partial':
        return 'phone';
      case 'email':
        return 'email';
      case 'random':
        return 'random_key';
      default:
        return null;
    }
  }

  /** e.g. `common.pixKeyTypes.phone` — use with `translate` pipe in template. */
  get confirmedPixKeyTypeTranslationKey(): string | null {
    const k = this.confirmedPixKeyCommonTypeKey;
    return k ? `enums.pixKeyTypes.${k}` : null;
  }

  /** How to render the primary PIX value on the confirm row (`document` / `phone` use pipes). */
  get confirmedPixKeyDisplayMode(): 'document' | 'phone' | 'plain' {
    switch (this.effectiveRecipientKind) {
      case 'cpf':
      case 'cpf_partial':
      case 'cnpj':
        return 'document';
      case 'phone':
      case 'phone_partial':
        return 'phone';
      default:
        return 'plain';
    }
  }

  /** Digit-only input for `document` / `phone` pipes on the confirm step. */
  get confirmedPixKeyDigitsForPipe(): string {
    return this.confirmedPixKeyDisplay.replace(/\D/g, '');
  }

  /** Holder doc / agency / account from verification — show when any present. */
  get hasConfirmedRecipientBankDetails(): boolean {
    const r = this.pixRecipientResponse;
    if (!r) {
      return false;
    }
    const doc = String(r.holderDocument ?? '').trim();
    const ag = String(r.agency ?? '').trim();
    const ac = String(r.account ?? '').trim();
    return !!(doc || ag || ac);
  }

  /** From confirm step: return to PIX key entry (same as cancel confirmed). */
  goBackToRecipientSearchFromConfirmed(): void {
    this.cancelConfirmedRecipient();
  }

  readonly formatRecipientAgency = formatRecipientAgency;
  readonly formatRecipientAccount = formatRecipientAccount;

  private readonly settingsService = inject(SettingsService);
  private readonly paymentReceiptExport = inject(PaymentReceiptExportService);

  private get branding(): PublicSettings['branding'] | undefined {
    return this.settingsService.get('branding') as
      | PublicSettings['branding']
      | undefined;
  }

  get exportLogoUrl(): string | null {
    const style = this.branding?.style;
    if (!style) return null;
    return style.logoBlack || style.logo || null;
  }

  get exportBrandPrimaryColor(): string {
    return this.branding?.style?.primaryColor || '#ff5c00';
  }

  get exportDomainTitle(): string {
    return this.branding?.page?.title?.trim() ?? '';
  }

  get exportProviderLabel(): string {
    const provider = this.paymentMetadata?.provider;
    if (provider === null || provider === undefined) return '';
    const slug = String(provider).trim().toLowerCase();
    if (!slug) return '';
    const key = `enums.paymentProvider.${slug}`;
    const translated = this.translate.instant(key);
    if (!translated || translated === key)
      return this.translate.instant('common.unknown');
    return translated;
  }

  get exportAgencyDisplay(): string {
    return formatRecipientAgency(this.paymentReceipt?.receiver?.agency);
  }

  get exportAccountDisplay(): string {
    return formatRecipientAccount(this.paymentReceipt?.receiver?.account);
  }

  isDownloadingReceipt = false;

  async downloadReceiptPng(): Promise<void> {
    const receipt = this.paymentReceipt;
    if (!receipt || this.isDownloadingReceipt) {
      return;
    }
    this.isDownloadingReceipt = true;
    try {
      const idPart = String(receipt.e2eId ?? receipt.id ?? 'pix').replace(
        /[^\w.-]+/g,
        '_',
      );
      await this.paymentReceiptExport.downloadPng(
        {
          receipt,
          amount: this.amountBreakdown?.amount ?? null,
          requestedAmount: this.amountBreakdown?.amount ?? null,
          calculatedFee: this.amountBreakdown?.calculatedFee ?? null,
          finalAmount: this.amountBreakdown?.finalAmount ?? null,
          recipientDisplayName: this.resolvedRecipientName,
          bankDisplayLine: this.confirmedBankDisplayLine,
          providerLabel: this.exportProviderLabel,
          agencyDisplay: this.exportAgencyDisplay,
          accountDisplay: this.exportAccountDisplay,
          logoUrl: this.exportLogoUrl,
          brandPrimaryColor: this.exportBrandPrimaryColor,
          domainTitle: this.exportDomainTitle,
        },
        `comprovante-pix-${idPart}.png`,
      );
    } catch {
      this.alerts
        .open(
          this.translate.instant('common.paymentReceipt.export.downloadError'),
          {
            appearance: 'error',
          },
        )
        .subscribe();
    } finally {
      this.isDownloadingReceipt = false;
    }
  }

  get recipientKindHint(): string | null {
    if (this.recipientInferredKind === 'cpf_or_phone') {
      return null;
    }
    switch (this.effectiveRecipientKind) {
      case 'cpf':
      case 'cpf_partial':
      case 'cnpj':
        return this.translate.instant('enums.pixKeyTypes.document');
      case 'phone':
      case 'phone_partial':
        return this.translate.instant('enums.pixKeyTypes.phone');
      case 'email':
        return this.translate.instant('enums.pixKeyTypes.email');
      case 'random':
        return this.translate.instant('enums.pixKeyTypes.random_key');
      default:
        return null;
    }
  }

  get pixAmountCustomMessages(): Record<string, string> {
    const p = 'features.withdrawals.modalPix';
    const maxStr =
      typeof this.walletAvailableBalance === 'number' &&
      Number.isFinite(this.walletAvailableBalance)
        ? this.formatLimitCurrency(this.walletAvailableBalance)
        : '';
    const txLimit = this.recipientLimits?.cashoutTransactionLimit;
    const txLimitStr =
      txLimit !== null && txLimit !== undefined && Number.isFinite(txLimit)
        ? this.formatLimitCurrency(txLimit)
        : '';
    const dailyRemaining = this.recipientLimits?.dailyCashoutRemaining;
    const dailyRemainingStr =
      dailyRemaining !== null &&
      dailyRemaining !== undefined &&
      Number.isFinite(dailyRemaining)
        ? this.formatLimitCurrency(dailyRemaining)
        : '';
    const feeMinStr = this.formatLimitCurrency(this.feeForDeductModeDisplay());
    return {
      min: this.translate.instant(`${p}.customMessages.moreThanOne`),
      amountExceedsBalance: this.translate.instant(
        `${p}.customMessages.amountExceedsBalance`,
        {max: maxStr},
      ),
      amountExceedsTransactionLimit: this.translate.instant(
        `${p}.customMessages.amountExceedsTransactionLimit`,
        {max: txLimitStr},
      ),
      amountExceedsDailyRemaining: this.translate.instant(
        `${p}.customMessages.amountExceedsDailyRemaining`,
        {max: dailyRemainingStr},
      ),
      amountBelowFee: this.translate.instant(
        `${p}.customMessages.amountBelowFee`,
        {min: feeMinStr},
      ),
    };
  }

  /** Shown under the amount field on the value step when a daily cap applies. */
  get dailyRemainingHint(): string | null {
    const remaining = this.recipientLimits?.dailyCashoutRemaining;
    if (remaining === null || remaining === undefined) {
      return null;
    }
    return this.translate.instant(
      'features.withdrawals.modalPix.dailyLimitRemainingHint',
      {remaining: this.formatLimitCurrency(remaining)},
    );
  }

  private loadRecipientLimits(): void {
    this.recipientsSettingsService
      .findLimits()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(() => of(null)),
      )
      .subscribe((snapshot: RecipientLimitsSnapshot | null) => {
        this.recipientLimits = snapshot;
        const amountCtrl = this.pixTransferForm.get('amount');
        if (amountCtrl && this.recipientStepStatus === 'value') {
          amountCtrl.updateValueAndValidity({emitEvent: false});
        }
      });
  }

  private formatLimitCurrency(value: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  }

  /** Fee shown in amountBelowFee message (based on current input or a nominal amount). */
  private feeForDeductModeDisplay(): number {
    const entered = this.coerceAmount(
      this.pixTransferForm.get('amount')?.value,
    );
    const basis = entered !== null && entered > 0 ? entered : 1;
    return calculateWithdrawalAmounts(basis, this.feeSnapshot, 'deductFees')
      .calculatedFee;
  }

  private loadRecipientWallet(): void {
    this.walletsService
      .findAll(undefined, true)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(() => of(null)),
      )
      .subscribe((response: unknown) => {
        const wallets =
          response === null ? [] : this.normalizeWalletResponse(response);
        const primary = wallets.find(w => w.isPrimary) ?? wallets[0];
        let balance = primary
          ? this.coerceNumericBalance(primary.availableBalance)
          : null;
        if (balance === null) {
          balance = this.balanceFromStoredProfile();
        }
        this.walletAvailableBalance = balance;
        const amountCtrl = this.pixTransferForm.get('amount');
        if (amountCtrl && this.recipientStepStatus === 'value') {
          amountCtrl.updateValueAndValidity({emitEvent: false});
        }
      });
  }

  /** API may return `T[]`, `{ records: T[] }`, or a single wallet-shaped object. */
  private normalizeWalletResponse(body: unknown): Wallet[] {
    if (Array.isArray(body)) {
      return body as Wallet[];
    }
    if (
      body &&
      typeof body === 'object' &&
      'records' in body &&
      Array.isArray((body as {records: unknown}).records)
    ) {
      return (body as {records: Wallet[]}).records;
    }
    if (
      body &&
      typeof body === 'object' &&
      'availableBalance' in (body as Record<string, unknown>)
    ) {
      return [body as Wallet];
    }
    return [];
  }

  private coerceNumericBalance(value: unknown): number | null {
    if (value === null || value === undefined) {
      return null;
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed.length) {
        return null;
      }
      const n = Number(trimmed);
      return Number.isFinite(n) ? n : null;
    }
    return null;
  }

  private balanceFromStoredProfile(): number | null {
    try {
      const profile = JSON.parse(readProfileRaw()) as {
        recipient?: {wallet?: {availableBalance?: unknown}};
      };
      return this.coerceNumericBalance(
        profile.recipient?.wallet?.availableBalance,
      );
    } catch {
      return null;
    }
  }

  private grossAmountFromControl(control: AbstractControl): number | null {
    const n = this.coerceAmount(control.value);
    if (n === null) {
      return null;
    }
    const mode = this.amountInputMode;
    return calculateWithdrawalAmounts(n, this.feeSnapshot, mode).amount;
  }

  private get isSavedBankAccountTransfer(): boolean {
    return this.savedBankAccountSelection !== null;
  }

  private amountWithinWalletValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const max = this.walletAvailableBalance;
      if (max === null || !Number.isFinite(max)) {
        return null;
      }
      const n = this.grossAmountFromControl(control);
      if (n === null) {
        return null;
      }
      if (n > max + 1e-9) {
        return {amountExceedsBalance: true};
      }
      return null;
    };
  }

  private amountWithinTransactionLimitValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (this.isSavedBankAccountTransfer) {
        return null;
      }
      const max = this.recipientLimits?.cashoutTransactionLimit;
      if (max === null || max === undefined || !Number.isFinite(max)) {
        return null;
      }
      const n = this.grossAmountFromControl(control);
      if (n === null) {
        return null;
      }
      if (n > max + 1e-9) {
        return {amountExceedsTransactionLimit: true};
      }
      return null;
    };
  }

  private amountWithinDailyRemainingValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const max = this.recipientLimits?.dailyCashoutRemaining;
      if (max === null || max === undefined || !Number.isFinite(max)) {
        return null;
      }
      const n = this.grossAmountFromControl(control);
      if (n === null) {
        return null;
      }
      if (n > max + 1e-9) {
        return {amountExceedsDailyRemaining: true};
      }
      return null;
    };
  }

  /** deductFees mode: wallet debit must exceed the fee so the recipient receives a payout. */
  private amountCoversFeeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (this.amountInputMode !== 'deductFees') {
        return null;
      }
      const n = this.coerceAmount(control.value);
      if (n === null) {
        return null;
      }
      const {calculatedFee} = calculateWithdrawalAmounts(
        n,
        this.feeSnapshot,
        'deductFees',
      );
      if (calculatedFee <= 0) {
        return null;
      }
      if (n <= calculatedFee + 1e-9) {
        return {amountBelowFee: true};
      }
      return null;
    };
  }

  private coerceAmount(raw: unknown): number | null {
    if (raw === null || raw === undefined || raw === '') {
      return null;
    }
    const n = typeof raw === 'number' ? raw : Number(raw);
    return Number.isFinite(n) ? n : null;
  }

  get recipientPixKeyCustomMessages(): Record<string, string> {
    const p = 'features.withdrawals.modalPix';
    return {
      recipientIncomplete: this.translate.instant(
        `${p}.smartRecipientErrors.recipientIncomplete`,
      ),
      recipientUnrecognized: this.translate.instant(
        `${p}.smartRecipientErrors.recipientUnrecognized`,
      ),
      invalidPhone: this.translate.instant(
        `${p}.smartRecipientErrors.invalidPhone`,
      ),
      invalidEmail: this.translate.instant(
        `${p}.smartRecipientErrors.invalidEmail`,
      ),
      invalidCPF: this.translate.instant(`${p}.customMessages.invalidCPF`),
      invalidCNPJ: this.translate.instant(`${p}.customMessages.invalidCNPJ`),
      invalidUuid: this.translate.instant(`${p}.customMessages.invalidUuid`),
    };
  }

  cancelConfirmedRecipient(): void {
    this.recipientStepStatus = 'unlocked';
    this.emitEmbeddedTitleKey();
    this.pixRecipientResponse = null;
    this.lastVerifiedKey = '';
    this.pixRecipientLookupErrorMessage = null;
  }

  goToValueStep(): void {
    this.valueStepViewReady = false;
    const amountCtrl = this.pixTransferForm.get('amount');
    if (!this.hasEmvFixedAmount) {
      amountCtrl?.enable({emitEvent: false});
    }
    amountCtrl?.setValidators([
      Validators.required,
      Validators.min(0.01),
      this.amountWithinWalletValidator(),
      this.amountWithinTransactionLimitValidator(),
      this.amountWithinDailyRemainingValidator(),
      this.amountCoversFeeValidator(),
    ]);
    amountCtrl?.updateValueAndValidity({emitEvent: false});
    this.recipientStepStatus = 'value';
    this.emitEmbeddedTitleKey();
    this.shellFooterChange.emit();
    this.cdr.detectChanges();
  }

  get scheduleCalendarMin(): TuiDay {
    return TuiDay.fromLocalNativeDate(new Date());
  }

  get scheduleDateAsTuiDay(): TuiDay | null {
    const raw = this.pixTransferForm.get('scheduledDate')?.value;
    if (!raw || typeof raw !== 'string') {
      return null;
    }
    const d = new Date(`${raw}T12:00:00`);
    return Number.isNaN(d.getTime()) ? null : TuiDay.fromLocalNativeDate(d);
  }

  get scheduleWhenDisplay(): string {
    const t = this.scheduleDateAsTuiDay;
    const repeats = !!this.pixTransferForm.get('repeatTransfer')?.value;
    const suffixKey = repeats
      ? 'features.withdrawals.modalPix.scheduleSuffixRepeating'
      : 'features.withdrawals.modalPix.scheduleSuffixNoRepeat';
    const suffix = this.translate.instant(suffixKey);
    if (!t) {
      const whenNow = this.translate.instant(
        'features.withdrawals.modalPix.scheduleWhenNow',
      );
      return `${whenNow}, ${suffix}`;
    }
    return `${t.getFormattedDay('DMY', '/')}, ${suffix}`;
  }

  get paymentReceiptDateDisplay(): string {
    const scheduled = this.pixTransferForm.get('scheduledDate')?.value;
    const baseDate =
      typeof scheduled === 'string' && scheduled.trim().length > 0
        ? new Date(`${scheduled}T12:00:00`)
        : new Date();

    if (Number.isNaN(baseDate.getTime())) {
      return '';
    }

    const monthMap = [
      'JAN.',
      'FEV.',
      'MAR.',
      'ABR.',
      'MAI.',
      'JUN.',
      'JUL.',
      'AGO.',
      'SET.',
      'OUT.',
      'NOV.',
      'DEZ.',
    ];

    const day = String(baseDate.getDate()).padStart(2, '0');
    const month = monthMap[baseDate.getMonth()] ?? '';
    const year = String(baseDate.getFullYear());

    return `${day} ${month} ${year}`;
  }

  onScheduleDayClick(day: TuiDay): void {
    const iso = day.getFormattedDay('YMD', '-');
    this.pixTransferForm.get('scheduledDate')?.setValue(iso);
  }

  get canSaveSchedule(): boolean {
    return !!this.pixTransferForm.get('scheduledDate')?.value;
  }

  saveSchedule(): void {
    if (!this.canSaveSchedule) {
      return;
    }
    this.scheduleDateDropdownOpen = false;
  }

  private transformPixKeyType(type: string): PixKeyTypeType {
    switch (type) {
      case 'cpf':
      case 'cnpj':
        return PixKeyTypeType.DOCUMENT;
      case 'email':
        return PixKeyTypeType.EMAIL;
      case 'phone':
        return PixKeyTypeType.PHONE;
      case 'random':
        return PixKeyTypeType.RANDOM_KEY;
      default:
        throw new Error(`Invalid pix key type: ${type}`);
    }
  }

  private clearSavedBankAccountSelectionIfPixKeyChanged(trimmed: string): void {
    const sel = this.savedBankAccountSelection;
    if (!sel) {
      return;
    }
    const currentType = String(
      this.pixTransferForm.get('pixKeyType')?.value ?? sel.pixKeyType,
    );
    const current = this.sanitizePixKeyForPayload(trimmed, currentType);
    const saved = this.sanitizePixKeyForPayload(sel.pixKey, sel.pixKeyType);
    if (current !== saved) {
      this.savedBankAccountSelection = null;
    }
  }

  private sanitizePixKeyForPayload(pixKey: string, type: string): string {
    const value = String(pixKey ?? '');
    const normalized = String(type ?? '').toLowerCase();
    if (normalized === 'phone' || normalized === PixKeyTypeType.PHONE) {
      return sanitizePixKeyForApi(value, PixKeyTypeType.PHONE);
    }
    if (
      normalized === 'cpf' ||
      normalized === 'cnpj' ||
      normalized === 'document' ||
      normalized === PixKeyTypeType.DOCUMENT
    ) {
      return sanitizePixKeyForApi(value, PixKeyTypeType.DOCUMENT);
    }
    if (normalized === 'email' || normalized === PixKeyTypeType.EMAIL) {
      return sanitizePixKeyForApi(value, PixKeyTypeType.EMAIL);
    }
    if (
      normalized === 'random' ||
      normalized === 'random_key' ||
      normalized === PixKeyTypeType.RANDOM_KEY
    ) {
      return sanitizePixKeyForApi(value, PixKeyTypeType.RANDOM_KEY);
    }
    return value.trim();
  }

  onWizardFormSubmit(event: Event): void {
    event.preventDefault();
    if (this.recipientStepStatus === 'review' && !this.isSubmitting) {
      this.onSubmit();
    }
  }

  onSubmit(): void {
    if (this.emvHandoff) {
      if (!this.isAmountControlValid() || this.isSubmitting) {
        this.pixTransferForm.get('amount')?.markAsTouched();
        return;
      }
    } else if (this.pixTransferForm.invalid || this.isSubmitting) {
      Object.keys(this.pixTransferForm.controls).forEach(key => {
        this.pixTransferForm.get(key)?.markAsTouched();
      });
      return;
    }
    this.isSubmitting = true;
    this.transferErrorMessage = null;
    const {pixKeyType, amount, pixKey, document} = this.pixTransferForm.value;
    const formPixKeyType = String(pixKeyType ?? '');
    const payloadAmount = this.coerceAmount(amount) ?? 0;
    const trimmedDescription =
      this.transferDescription ||
      String(this.pixTransferForm.get('description')?.value ?? '').trim();
    let payloadPix: PayloadPix;
    if (this.emvHandoff) {
      const decodedAmount = this.emvHandoff.decoded.amount;
      const emvAmount =
        typeof decodedAmount === 'number' && Number.isFinite(decodedAmount)
          ? decodedAmount
          : this.coerceAmount(this.pixTransferForm.get('amount')?.value) ?? 0;
      payloadPix = {
        amount: emvAmount,
        emvCode: this.emvHandoff.emvCode,
      };
    } else if (this.savedBankAccountSelection) {
      payloadPix = {
        amount: payloadAmount,
        recipientBankAccountId: this.savedBankAccountSelection.id,
      };
    } else {
      const e2eId = String(this.pixRecipientResponse?.pixKeyE2eId ?? '').trim();
      const bankAccount: NonNullable<PayloadPix['bankAccount']> = {
        pixKey: this.sanitizePixKeyForPayload(
          String(pixKey ?? ''),
          formPixKeyType,
        ),
        pixKeyType: this.transformPixKeyType(formPixKeyType),
        holderDocument: String(document ?? '').replace(/\D/g, ''),
      };
      if (e2eId.length > 0) {
        bankAccount.pixKeyE2eId = e2eId;
      }
      payloadPix = {
        amount: payloadAmount,
        bankAccount,
      };
    }
    if (trimmedDescription) {
      payloadPix.description = trimmedDescription;
    }
    this.crudService
      .createWith2Fa(payloadPix as unknown as Withdrawal, true)
      .subscribe({
        next: (withdrawal: Withdrawal) => {
          this.isSubmitting = false;
          this.createdWithdrawal = withdrawal;
          this.recipientStepStatus = 'payment_proof';
          this.emitEmbeddedTitleKey();
          if (
            withdrawal?.id &&
            (withdrawal.status === WithdrawalStatus.PENDING ||
              withdrawal.status === WithdrawalStatus.PROCESSING)
          ) {
            this.startPolling(withdrawal.id);
          } // Do not emit `completed` here: parent (`pix-area`) runs `finishClose()` which
          // resets this form and clears `pixRecipientResponse`, breaking the payment-proof step.
          // Emit only from `onProofConclude()` after the user dismisses the receipt.
        },
        error: (err: HttpErrorResponse | Error) => {
          this.isSubmitting = false;
          if (err instanceof Error && err.message === twoFactorCancelledError) {
            return;
          }
          if (
            err instanceof Error &&
            err.message === twoFactorMaxAttemptsError
          ) {
            this.alerts
              .open(
                this.translate.instant('common.twoFactor.maxAttemptsExceeded'),
                {
                  label: this.translate.instant(
                    'features.withdrawals.modalPix.swalMessages.error',
                  ),
                  appearance: 'error',
                },
              )
              .subscribe();
            return;
          }

          const withdrawal = this.parseWithdrawalFromHttpError(err);

          if (withdrawal) {
            this.createdWithdrawal = withdrawal;
            this.recipientStepStatus = 'payment_proof';
            this.emitEmbeddedTitleKey();
            return;
          }

          this.transferErrorMessage = this.resolveSubmitErrorMessage(err, null);
        },
      });
  }

  private parseWithdrawalFromHttpError(
    err: HttpErrorResponse | Error,
  ): Withdrawal | null {
    if (!(err instanceof HttpErrorResponse)) {
      return null;
    }

    const body = err.error;
    if (!body || typeof body !== 'object') {
      return null;
    }

    const direct = body as Partial<Withdrawal>;
    if (
      direct.paymentMetadata !== undefined ||
      direct.status !== undefined ||
      direct.id !== undefined
    ) {
      return direct as Withdrawal;
    }

    const nested = (body as {data?: Partial<Withdrawal>}).data;
    if (
      nested &&
      (nested.paymentMetadata !== undefined ||
        nested.status !== undefined ||
        nested.id !== undefined)
    ) {
      return nested as Withdrawal;
    }

    return null;
  }

  private resolveSubmitErrorMessage(
    err: HttpErrorResponse | Error,
    withdrawal: Withdrawal | null,
  ): string {
    const fallback = this.translate.instant(
      'features.withdrawals.modalPix.swalMessages.texts.2FMessageError',
    );

    if (withdrawal) {
      return getWithdrawalStatusErrorMessage(withdrawal, this.translate);
    }

    if (err instanceof HttpErrorResponse) {
      const body = err.error;
      if (typeof body === 'string' && body.trim()) {
        return body;
      }
      if (body && typeof body === 'object') {
        const providerError = extractPaymentMetadataError(
          (body as Withdrawal).paymentMetadata ??
            (body as {paymentMetadata?: Withdrawal['paymentMetadata']})
              .paymentMetadata,
        );
        if (providerError) {
          return providerError;
        }
        const message = (body as {message?: string}).message;
        if (message?.trim()) {
          return message;
        }
      }
    }

    return fallback;
  }

  get paymentReceipt(): PaymentReceipt | undefined {
    return this.createdWithdrawal?.paymentMetadata?.receipt ?? undefined;
  }

  /** Pre-submit estimates from snapshot; post-submit prefers API withdrawal fields. */
  get amountBreakdown(): WithdrawalAmountBreakdown | null {
    if (this.createdWithdrawal) {
      return resolveWithdrawalAmounts(this.createdWithdrawal, this.feeSnapshot);
    }
    const raw = this.pixTransferForm?.get('amount')?.value;
    const amount = Number(raw);
    if (!Number.isFinite(amount) || amount <= 0) {
      return null;
    }
    const mode = this.amountInputMode;
    return calculateWithdrawalAmounts(amount, this.feeSnapshot, mode);
  }

  get amountInputMode(): WithdrawalAmountInputMode {
    if (this.emvHandoff) {
      return 'addFees';
    }
    return this.addWithdrawalFeeToAmount ? 'addFees' : 'deductFees';
  }

  get amountFieldLabelKey(): string {
    return this.amountInputMode === 'addFees'
      ? 'features.withdrawals.modalPix.amountInputMode.amountLabelRecipientReceives'
      : 'features.withdrawals.modalPix.amountInputMode.amountLabelWalletDebit';
  }

  get amountFieldHint(): {message: string; icon: string} {
    return {
      message:
        this.amountInputMode === 'addFees'
          ? 'features.withdrawals.modalPix.amountInputMode.fieldHintAddFees'
          : 'features.withdrawals.modalPix.amountInputMode.fieldHintDeductFees',
      icon: '@phi.info',
    };
  }

  get paymentMetadata(): PaymentProviderResponse<unknown, unknown> | undefined {
    return this.createdWithdrawal?.paymentMetadata as
      | PaymentProviderResponse<unknown, unknown>
      | undefined;
  }

  get isWithdrawalPolling(): boolean {
    const status = this.createdWithdrawal?.status;
    return (
      status === WithdrawalStatus.PENDING ||
      status === WithdrawalStatus.PROCESSING
    );
  }

  get isWithdrawalSuccess(): boolean {
    return this.createdWithdrawal?.status === WithdrawalStatus.SUCCESS;
  }

  get isWithdrawalFailed(): boolean {
    const status = this.createdWithdrawal?.status;
    return (
      status === WithdrawalStatus.FAILED ||
      status === WithdrawalStatus.CANCELED ||
      status === WithdrawalStatus.REJECTED ||
      status === WithdrawalStatus.REFUNDED
    );
  }

  get withdrawalStatusErrorMessage(): string | null {
    if (this.transferErrorMessage) {
      return this.transferErrorMessage;
    }

    if (!this.createdWithdrawal || !this.isWithdrawalFailed) {
      return null;
    }

    return getWithdrawalStatusErrorMessage(
      this.createdWithdrawal,
      this.translate,
    );
  }

  get showWithdrawalFailureUi(): boolean {
    return this.isWithdrawalFailed || !!this.withdrawalStatusErrorMessage;
  }

  private startPolling(id: number): void {
    this.pollingSub?.unsubscribe();
    this.pollingSub = interval(5000)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(() => this.withdrawalsService.findOne(id)),
      )
      .subscribe({
        next: (w: Withdrawal) => {
          this.createdWithdrawal = w;
          if (
            w.status !== WithdrawalStatus.PENDING &&
            w.status !== WithdrawalStatus.PROCESSING
          ) {
            this.stopPolling();
          }
        },
      });
  }

  private stopPolling(): void {
    this.pollingSub?.unsubscribe();
    this.pollingSub = undefined;
  }

  onProofConclude(): void {
    this.reset();
    this.completed.emit();
  }

  /** Called by shell when leaving transfer flow or closing modal; clears internal state. */
  reset(): void {
    this.emvHandoff = null;
    this.stopPolling();
    this.createdWithdrawal = null;
    this.transferErrorMessage = null;
    this.contactSearchQuery = '';
    this.recipientStepStatus = 'unlocked';
    this.isSubmitting = false;
    this.scheduleDateDropdownOpen = false;
    this.recipientMask = '';
    this.recipientInferredKind = 'unknown';
    this.manualPixKeyKind = null;
    this.lastVerifiedKey = '';
    this.pixKeyFocused = false;
    this.valueStepViewReady = false;
    this.transferDescription = '';
    this.pixRecipientLookupPending = false;
    this.pixRecipientLookupErrorMessage = null;
    this.pixRecipientResponse = null;
    this.savedBankAccountSelection = null;

    this.pixTransferForm.reset(
      {
        pixKey: '',
        pixKeyType: '',
        amount: '',
        document: '',
        description: '',
        scheduledDate: null,
        repeatTransfer: false,
      },
      {emitEvent: false},
    );

    this.pixTransferForm
      .get('pixKey')
      ?.setValidators([
        Validators.required,
        this.validatePixKeyDynamic.bind(this),
      ]);
    this.pixTransferForm.get('amount')?.clearValidators();
    this.pixTransferForm.get('document')?.clearValidators();
    this.pixTransferForm.get('amount')?.enable({emitEvent: false});
    this.pixTransferForm
      .get('pixKey')
      ?.updateValueAndValidity({emitEvent: false});
    this.pixTransferForm
      .get('amount')
      ?.updateValueAndValidity({emitEvent: false});
    this.pixTransferForm
      .get('document')
      ?.updateValueAndValidity({emitEvent: false});
    this.pixTransferForm.markAsPristine();
    this.pixTransferForm.markAsUntouched();
    this.emitEmbeddedTitleKey();
  }
}
